import React, { useMemo, useState } from 'react';
import { Button, Icon, message, Pagination, Select, Tag, Tooltip, Upload } from 'antd';
import { useTranslation, Trans } from 'react-i18next';
import AdGrid from '../../../adLibrary/components/AdGrid';
import FoldersGroup from '../../../adLibrary/components/FoldersGroup';
import CreateFolderModal from '../../../adLibrary/components/modals/CreateFolderModal';
import BreadcrumbEllipsis from '../../../adLibrary/components/BreadcrumbEllipsis';
import { mapContentToAd, mapTagToFolder } from '../../../adLibrary/utils/adapters';
import { collapsePath } from '../../../adLibrary/utils/folderTree';
import { translateTagName } from '../../hooks/contentManager/useLibraryContents';
import { ACCEPT, MAX_SIZE_MB } from '../../../adLibrary/utils/fileValidation';
import useMergedDropzoneRef from '../../../adLibrary/utils/useMergedDropzoneRef';
import SearchInput from '../../../../shared/components/SearchInput';
import AdsSectionHolder from '../../../adLibrary/styles/adsSectionStyle';
import HelpTooltipIcon, { HelpIcon } from '../../../../shared/components/HelpTooltipIcon';
import OptionModal from '../../../../shared/components/OptionModal';
import VersionEnum from '../../../../../constants/VersionEnum';

const { Option } = Select;
const STATUS_COLOR = { available: 'green', 'in-use': 'blue', unavailable: 'red' };
const STATUS_LABEL = { available: 'Available', 'in-use': 'In use', unavailable: 'Not available' };

const LibraryBrowserComponent = ({
  rawContents = [],
  loading = false,
  folders = [],
  selectedFolder,
  folderPath = [],
  onSelectFolder,
  onSearch,
  onAddContent,
  onPreviewAd,
  uploadProps,
  onAddAllFromFolder,
  loadingAddAll = false,
  isFolderUsed = false,
  totalContent = 0,
  pageContents = 1,
  lengthPage = 23,
  onPageChange,
  typeFilter,
  onTypeFilterChange,
  sort,
  onSortChange,
  onUploadFiles,
  uploading = false,
  checkContentsAdded,
  canUploadToFolder = true,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolders,
  folderSubmitting = false,
  containerRef,
  rowCount,
  rowOptions = [],
  onRowCountChange,
  isMobile = false,
  activeVersion,
  onSelectVersion,
  showVersionSelect = false,
}) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('grid');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dragFolderId, setDragFolderId] = useState(null);
  const [folderModal, setFolderModal] = useState(null);
  const [folderModalPayload, setFolderModalPayload] = useState(null);

  const ads = useMemo(() => rawContents.map(mapContentToAd), [rawContents]);
  const rawById = useMemo(() => {
    const map = {};
    rawContents.forEach((c) => { map[c.id] = c; });
    return map;
  }, [rawContents]);
  const mappedFolders = useMemo(() => folders.map(mapTagToFolder), [folders]);
  const collapsedFolderPath = useMemo(() => collapsePath(folderPath), [folderPath]);

  const folderIdAt = (e) => {
    const el = e.target.closest && e.target.closest('[data-folder-drop]');
    return el && el.getAttribute('data-folder-id') ? Number(el.getAttribute('data-folder-id')) : null;
  };

  const { rootProps: dropzoneRootProps, mergedRef: mergedContainerRef, isDragActive } = useMergedDropzoneRef(
    {
      noClick: true,
      noKeyboard: true,
      multiple: true,
      accept: ACCEPT,
      maxSize: MAX_SIZE_MB * 1024 * 1024,
      disabled: !onUploadFiles || uploading,
      onDrop: (accepted, rejections, event) => {
        setDragFolderId(null);
        if (rejections.length) message.error(t('Supports JPG, PNG, MP4, MP3 (Max 500MB)'));
        if (!accepted.length) return;
        onUploadFiles(accepted, folderIdAt(event) || undefined);
      },
    },
    containerRef
  );

  const rootProps = {
    ...dropzoneRootProps,
    className: `ads-dropzone${isDragActive && dragFolderId === null ? ' drag-over' : ''}`,
    onDragOver: (e) => {
      if (dropzoneRootProps.onDragOver) dropzoneRootProps.onDragOver(e);
      if (isDragActive) setDragFolderId(folderIdAt(e));
    },
    onDragLeave: (e) => {
      if (dropzoneRootProps.onDragLeave) dropzoneRootProps.onDragLeave(e);
      if (!e.currentTarget.contains(e.relatedTarget)) setDragFolderId(null);
    },
  };

  const dropDestination = selectedFolder && selectedFolder.label ? selectedFolder.label : t('My ads');

  const previewIcon = (ad) =>
    ad.type === 'image' ? 'file-image' : ad.type === 'video' ? 'video-camera' : 'eye';

  const isAdAdded = (ad) => !!(checkContentsAdded && checkContentsAdded(ad.id));

  const getCardActions = (ad) => {
    const added = isAdAdded(ad);
    return [
      added
        ? { key: 'added', icon: 'check', tooltip: t('Content added in campaign list'), disabled: true, variant: 'added' }
        : { key: 'add', icon: 'plus', tooltip: t('Add to version'), disabled: ad.pending, onClick: () => onAddContent(rawById[ad.id]) },
      { key: 'preview', icon: previewIcon(ad), tooltip: t('Preview'), onClick: () => onPreviewAd(rawById[ad.id]) },
    ];
  };

  const noFolderSelected = !selectedFolder || !selectedFolder.key;

  const openModal = (type, payload) => {
    setFolderModal(type);
    setFolderModalPayload(payload || null);
  };
  const closeFolderModal = () => {
    setFolderModal(null);
    setFolderModalPayload(null);
  };

  const extraColumns = [
    {
      title: t('Status'),
      key: 'status',
      width: 120,
      render: (_, ad) =>
        isAdAdded(ad) ? (
          <Tag color="blue">{t('Added').toUpperCase()}</Tag>
        ) : (
          <Tag color={STATUS_COLOR[ad.status]}>{t(STATUS_LABEL[ad.status] || ad.status).toUpperCase()}</Tag>
        ),
    },
  ];

  const renderRowActions = (ad) => {
    const added = isAdAdded(ad);
    return (
      <Tooltip title={added ? t('Content added in campaign list') : t('Add to version')}>
        <Button
          shape="circle"
          type="primary"
          ghost
          disabled={added || ad.pending}
          onClick={added ? undefined : () => onAddContent(rawById[ad.id])}
        >
          <Icon type={added ? 'check' : 'plus'} />
        </Button>
      </Tooltip>
    );
  };

  return (
    <div className="library-browser">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <HelpTooltipIcon
            href="https://www.youtube.com/watch?v=ouAACPF6syA"
            tooltip={<Trans i18nKey="Folder advertisement" components={{ icon: <HelpIcon /> }} />}
          />
          <span
            style={{ color: '#1890ff', cursor: noFolderSelected ? 'default' : 'pointer' }}
            onClick={() => onSelectFolder(null)}
          >
            {t('My ads')}
          </span>
          {collapsedFolderPath.map((item, index) => {
            if (item.ellipsis) {
              return (
                <React.Fragment key="ellipsis">
                  <Icon type="right" style={{ fontSize: 12, color: '#bfbfbf' }} />
                  <BreadcrumbEllipsis hidden={item.hidden} onNavigate={(folder) => onSelectFolder(folder.id)} />
                </React.Fragment>
              );
            }
            const folder = item.folder;
            const isCurrent = index === collapsedFolderPath.length - 1;
            return (
              <React.Fragment key={folder.id}>
                <Icon type="right" style={{ fontSize: 12, color: '#bfbfbf' }} />
                <span
                  style={{
                    color: isCurrent ? 'rgba(0,0,0,0.85)' : '#1890ff',
                    fontWeight: isCurrent ? 600 : 400,
                    cursor: isCurrent ? 'default' : 'pointer',
                  }}
                  onClick={isCurrent ? undefined : () => onSelectFolder(folder.id)}
                >
                  {translateTagName(folder.name, t)}
                </span>
              </React.Fragment>
            );
          })}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Tooltip title={<Trans i18nKey="I don’t have my ads tooltip" components={{ icon: <HelpIcon secondary /> }} />}>
            <Button
              type="primary"
              size="small"
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              onClick={() => window.open('https://www.youtube.com/watch?v=ABgBDytnAAk', '_blank')}
            >
              {t('I don’t have my ads')}
              <HelpIcon secondary />
            </Button>
          </Tooltip>
          {canUploadToFolder && uploadProps && (
            <Upload {...uploadProps} listType="text" showUploadList={false}>
              <Button>
                <Icon type="cloud-upload" /> {t('Upload ad')}
              </Button>
            </Upload>
          )}
          <Button type="primary" onClick={() => openModal('createFolder')}>
            <Icon type="plus" /> {t('New folder')}
          </Button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: filtersOpen ? 0 : 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          className={filtersOpen ? 'active' : undefined}
          style={filtersOpen ? { color: '#1890ff', borderColor: '#1890ff' } : undefined}
          onClick={() => setFiltersOpen((open) => !open)}
        >
          {t('Filters')} <Icon type="filter" />
        </Button>
        {showVersionSelect && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="selectTag">{t('Version')}:</span>
            <Select
              style={{ width: 150 }}
              showSearch
              value={activeVersion}
              onChange={onSelectVersion}
              filterOption={(input, option) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {VersionEnum.map((item) => (
                <Option key={item.key} value={item.key}>
                  {t(item.name)}
                </Option>
              ))}
            </Select>
          </div>
        )}
        <div style={{ flex: 1, minWidth: 200 }}>
          <SearchInput type="async" onChange={(e) => onSearch(e.target.value)} />
        </div>
        <Tooltip title={noFolderSelected ? t('Select a folder to add all its ads') : isFolderUsed ? t('All ads from this folder were already added in this session') : t('Add all ads from folder')}>
          <Button shape="circle" icon="folder-add" loading={loadingAddAll} disabled={noFolderSelected || totalContent === 0 || loadingAddAll || isFolderUsed} onClick={onAddAllFromFolder} />
        </Tooltip>
        <Button.Group>
          <Button type={viewMode === 'grid' ? 'primary' : 'default'} icon="appstore" onClick={() => setViewMode('grid')} />
          <Button type={viewMode === 'list' ? 'primary' : 'default'} icon="unordered-list" onClick={() => setViewMode('list')} />
        </Button.Group>
        <Select
          value={sort || undefined}
          onChange={onSortChange}
          placeholder={t('Sort')}
          allowClear
          style={{ minWidth: 150 }}
        >
          <Option value="newest">{t('Most recent')}</Option>
          <Option value="oldest">{t('Oldest')}</Option>
          <Option value="name">{t('Name')}</Option>
        </Select>
      </div>

      {filtersOpen && (
        <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 4, padding: 16, marginBottom: 16 }}>
          <div style={{ color: '#888', marginBottom: 6 }}>{t('Ad type')}</div>
          <Select
            value={typeFilter || undefined}
            onChange={onTypeFilterChange}
            placeholder={t('Ad type')}
            allowClear
            style={{ width: 200 }}
          >
            <Option value="image">{t('Image')}</Option>
            <Option value="video">{t('Video')}</Option>
          </Select>
        </div>
      )}

      <AdsSectionHolder>
        <div ref={mergedContainerRef} {...rootProps}>
          {isDragActive && dragFolderId === null && (
            <div className="ads-dropzone-overlay">
              <Icon type="cloud-upload" className="ads-dropzone-icon" />
              <span className="ads-dropzone-title">{t('Drop files to upload')}</span>
              <span className="ads-dropzone-hint">
                <Trans
                  i18nKey="They will be saved in {{folder}}"
                  values={{ folder: dropDestination }}
                  components={{ strong: <strong /> }}
                />
              </span>
            </div>
          )}

          <FoldersGroup
            folders={mappedFolders}
            viewMode={viewMode}
            selectedIds={[]}
            onSelectFolder={(folder) => onSelectFolder(folder ? folder.id : null)}
            dragFolderId={onUploadFiles ? dragFolderId : null}
            openModal={openModal}
          />

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Icon type="loading" style={{ fontSize: 24 }} />
            </div>
          ) : (
            <div className="ads-group">
              <AdGrid
                ads={ads}
                viewMode={viewMode}
                showStatus
                getActions={getCardActions}
                getAdded={isAdAdded}
                getDragData={(ad) => ({ rawContent: rawById[ad.id] })}
                onPreview={(ad) => onPreviewAd(rawById[ad.id])}
                extraColumns={extraColumns}
                renderRowActions={renderRowActions}
                columnLabels={{ name: t('Ad'), date: t('Date') }}
                showTableTitle={false}
                cardMinWidth={220}
              />
              {totalContent > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  <Pagination
                    className="ads-pagination"
                    current={pageContents}
                    pageSize={lengthPage}
                    total={totalContent}
                    onChange={onPageChange}
                    showSizeChanger={false}
                    showTotal={(total, range) => `${range[0]} - ${range[1]} ${t('of')} ${total}`}
                  />
                  {!isMobile && onRowCountChange && (
                    <Select
                      value={rowCount}
                      onChange={onRowCountChange}
                      style={{ width: 180, position: 'absolute', right: 0 }}
                    >
                      {rowOptions.map((opt) => (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </AdsSectionHolder>

      <CreateFolderModal
        visible={folderModal === 'createFolder' || folderModal === 'renameFolder'}
        folder={folderModal === 'renameFolder' ? folderModalPayload && folderModalPayload.folder : null}
        submitting={folderSubmitting}
        onSubmit={
          folderModal === 'renameFolder'
            ? (name) => {
                onRenameFolder(folderModalPayload.folder.id, name, folderModalPayload.folder.parentId);
                closeFolderModal();
              }
            : (name) => {
                onCreateFolder(name, selectedFolder && selectedFolder.key);
                closeFolderModal();
              }
        }
        onCancel={closeFolderModal}
      />

      <OptionModal
        visible={folderModal === 'deleteFolder'}
        title={t('Archive folder')}
        body={t('Are you sure you want to archive this folder?')}
        okText={t('Archive')}
        cancelText={t('Cancel')}
        onOk={() => {
          onDeleteFolders([folderModalPayload.folder.id]);
          closeFolderModal();
        }}
        onCancel={closeFolderModal}
        okButtonProps={{ type: 'danger' }}
      />
    </div>
  );
};

export default LibraryBrowserComponent;
